// @flow

import React, { useState, useEffect, useCallback } from "react";

import Modal, { OvalModalWrap, OvalModalCloseV2 } from "../../Modal";
import Button from "../../ButtonDefault";
import * as Style from "../styledComponents";

import apiMethod from "../../../api/apiMethod";
import { __ } from "../../../lib/translate";

type Props = {
  head: boolean,
  userId: number,
  userEmail: string,
  fName: string,
  lName: string,
  teamId: number,
  teamName: string
};

const SetTeamHeadModal = ({
  head = false,
  userId = 0,
  userEmail = "",
  fName = "",
  lName = "",
  teamId = 0,
  teamName = ""
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsOpen(true);
  }, []);

  const onEnter = useCallback(
    (e: any) => {
      if (e.keyCode === 13) {
        changeStatus();
      }
    },
    [changeStatus]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keyup", onEnter);
    } else {
      document.removeEventListener("keyup", onEnter);
    }
  }, [isOpen, onEnter]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const changeStatus = useCallback(() => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    let headStatus = 1;
    let successMessage = __("modal_set_team_head_message", "User set as head.");

    if (head) {
      headStatus = 0;
      successMessage = __("modal_set_team_head_message_removed", "User removed as head");
    }

    apiMethod(
      "account_teamlink_head",
      { userid: userId, teamid: teamId, head: headStatus },
      () => {
        PCB.refreshAll();
        HFN.message(__(successMessage));
        handleClose();
      },
      {
        errorCallback: ({ error, result }) => {
          setIsLoading(false);
          let errorMessage = __("something_went_wrong_refresh_and_try_again");

          if (result === 2076) {
            errorMessage = __("error_2076", "You do not have the permissions for this action.");
          }

          HFN.message(errorMessage, "error");
          throw new Error(error);
        }
      }
    );
  });

  return (
    <Modal animate onClose={handleClose} opened={isOpen}>
      <OvalModalWrap>
        <OvalModalCloseV2 onClick={handleClose} />
        <Style.Container>
          <Style.Header>{__("modal_set_team_head_header", "Change head status")}</Style.Header>
          <Style.Label>{head ? __("Remove team head") : __("Set as Head")}</Style.Label>
          <Style.Box>
            <Style.WrapperElements>
              <Style.ElementBold>{__("Name")}</Style.ElementBold>
              <Style.ElementBold>{__("Email")}</Style.ElementBold>
              <Style.ElementBold>{__("Team")}</Style.ElementBold>
            </Style.WrapperElements>
            <Style.WrapperElements>
              <Style.ElementUnderLine><b>{`${fName} ${lName}`}</b></Style.ElementUnderLine>
              <Style.ElementUnderLine>{userEmail}</Style.ElementUnderLine>
              <Style.ElementUnderLine>{teamName}</Style.ElementUnderLine>
            </Style.WrapperElements>
          </Style.Box>
          <Style.Footer>
            <Button
              color="lightgray4"
              style={{
                marginRight: "5px"
              }}
              onClick={handleClose}
            >
              {__("Cancel")}
            </Button>
            <Button
              loading={isLoading}
              color={head ? "red" : "cyan"}
              style={{
                marginLeft: "5px"
              }}
              disabledColor="#dddddd"
              onClick={changeStatus}
            >
              {head ? __("family_remove", "Remove") : __("set_button", "Set")}
            </Button>
          </Style.Footer>
        </Style.Container>
      </OvalModalWrap>
    </Modal>
  );
};

export default SetTeamHeadModal;
